//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect, useMemo } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Switch } from '../ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { getInitials } from '../../utils/avatarUtils';
import { stringToColor } from '../../utils/colorUtils';
import {
  UserAvatar,
  Asleep,
  Settings,
  Password,
  TrashCan,
} from '@carbon/icons-react';
import LogoutButton from '../card/logoutButton';
import { useTheme } from '../../ThemeContext';
import { useConfig } from '../../Config';
import { useUserAuth } from '../../UserAuth';
import { useTeamspaceContext } from '../../TeamspaceContext';
import CreateTokenModal from '../Token/CreateTokenModal';
import DeleteTokenModal from '../Token/DeleteTokenModal';
import TenantDataService from '../../utils/TenantDataService';

interface ProfileMenuProps {
  userName?: string;
  isAuthenticating: boolean;
  isLoading: boolean;
}

function ProfileMenu({
  userName,
  isAuthenticating,
  isLoading,
}: ProfileMenuProps) {
  const { t } = useTranslation();
  const { darkMode, toggleDarkMode } = useTheme();
  const { services } = useConfig();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useUserAuth();
  const { teamspaceId } = useTeamspaceContext();
  const showDarkMode = services?.darkMode?.show ?? true;
  const [createTokenOpen, setCreateTokenOpen] = useState(false);
  const [deleteTokenOpen, setDeleteTokenOpen] = useState(false);

  const tenantBaseUrl = services?.tenant?.url ?? '';
  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );
  const menuItems: never[] = [];

  return (
    <DropdownMenu>
      {!isAuthenticating && !isLoading && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  className="rounded p-2 transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1"
                  aria-label={t('Profile')}
                  id="id-login-logo"
                  data-testid="login-logo"
                  type="button"
                >
                  <UserAvatar size={20} className="text-white" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('Profile')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {!isAuthenticating && !isLoading && (
        <DropdownMenuContent
          className="w-64"
          id="profile-dropdown"
          data-testid="profile-dropdown"
        >
          {/* Header with User Info */}
          <div
            className="border-b border-gray-200 p-4 dark:border-gray-700"
            id="id-profile-menu-header"
            data-testid="profile-menu-header"
          >
            <div
              className="text-sm font-semibold text-gray-900 dark:text-white"
              id="id-profile-menu-title"
              data-testid="profile-menu-title"
            >
              {t('my-profile')}
            </div>
          </div>

          <div
            className="flex items-center gap-3 border-b border-gray-200 p-4 dark:border-gray-700"
            id="id-profile-menu-user-info"
            data-testid="profile-menu-user-info"
          >
            <Avatar
              className="size-10 shrink-0"
              style={{
                backgroundColor: stringToColor(
                  userName && userName.trim() !== '' ? userName : 'Guest',
                ),
              }}
              id="profile-first-item"
            >
              <AvatarFallback>{getInitials(userName, 'Guest')}</AvatarFallback>
            </Avatar>
            <div
              className="flex min-w-0 flex-1 flex-col"
              id="id-profile-menu-user-details"
              data-testid="profile-menu-user-details"
            >
              <div
                className="break-words text-sm font-medium text-gray-900 dark:text-white"
                id="id-profile-menu-username"
                data-testid="profile-menu-username"
              >
                {userName && userName.trim() !== '' ? userName : t('Guest')}
              </div>
            </div>
          </div>

          {/* Member Access — the user's own integration access and credentials.
              Replaces the former "Override Config" entry, which opened a modal that could
              only ever edit the user's active integration. Now that a user may reach
              several integrations through different groups, that modal is reached per-row
              from this page instead. */}
          {teamspaceId && user?.profile?.sub && (
            <DropdownMenuItem
              onClick={() => navigate('/member-access')}
              className="cursor-pointer px-4 py-3"
              data-testid="profile-menu-member-access-button"
            >
              <div className="flex items-center gap-2">
                <Settings
                  size={16}
                  className="text-gray-600 dark:text-gray-300"
                />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {t('userGroups.memberAccess') || 'Member Access'}
                </span>
              </div>
            </DropdownMenuItem>
          )}

          {/* Create Token */}
          <DropdownMenuItem
            onClick={() => setCreateTokenOpen(true)}
            className="cursor-pointer px-4 py-3"
            data-testid="profile-menu-create-token-button"
          >
            <div className="flex items-center gap-2">
              <Password
                size={16}
                className="text-gray-600 dark:text-gray-300"
              />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                {t('token.create.menuItem')}
              </span>
            </div>
          </DropdownMenuItem>

          {/* Delete Token */}
          <DropdownMenuItem
            onClick={() => setDeleteTokenOpen(true)}
            className="cursor-pointer px-4 py-3"
            data-testid="profile-menu-delete-token-button"
          >
            <div className="flex items-center gap-2">
              <TrashCan
                size={16}
                className="text-gray-600 dark:text-gray-300"
              />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                {t('token.delete.menuItem')}
              </span>
            </div>
          </DropdownMenuItem>

          {/* Dark Mode Toggle */}
          {showDarkMode && (
            <div
              className="border-y border-gray-200 px-4 py-3 dark:border-gray-700"
              id="id-profile-menu-dark-mode-section"
              data-testid="profile-menu-dark-mode-section"
            >
              <div
                className="flex items-center justify-between"
                id="id-profile-menu-dark-mode-toggle"
                data-testid="profile-menu-dark-mode-toggle"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="text-base"
                    id="id-profile-menu-dark-mode-icon"
                    data-testid="profile-menu-dark-mode-icon"
                  >
                    <Asleep
                      size={16}
                      className="text-gray-600 dark:text-gray-300"
                    />
                  </span>
                  <span
                    className="text-sm font-medium text-gray-600 dark:text-gray-300"
                    id="id-profile-menu-dark-mode-label"
                    data-testid="profile-menu-dark-mode-label"
                  >
                    {t('dark-mode')}
                  </span>
                </div>
                <Switch
                  checked={darkMode}
                  onCheckedChange={toggleDarkMode}
                  aria-label={t('dark-mode')}
                />
              </div>
            </div>
          )}

          {/* Logout Button */}
          <div
            className="p-3"
            id="id-profile-menu-logout-section"
            data-testid="profile-menu-logout-section"
          >
            <LogoutButton
              text={t('Logout')}
              className="ease w-full cursor-pointer rounded border-none bg-blue-500 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-blue-600 hover:shadow-md focus:shadow-lg focus:outline-none"
            />
          </div>
        </DropdownMenuContent>
      )}

      {/* Create Token Modal */}
      {user && (
        <CreateTokenModal
          isOpen={createTokenOpen}
          tenantDataService={tenantDataService}
          onClose={() => setCreateTokenOpen(false)}
        />
      )}

      {/* Delete Token Modal */}
      {user && (
        <DeleteTokenModal
          isOpen={deleteTokenOpen}
          tenantDataService={tenantDataService}
          onClose={() => setDeleteTokenOpen(false)}
        />
      )}
    </DropdownMenu>
  );
}

export default ProfileMenu;
